/**
 * LandingHeader
 *
 * Marketing site header with theme toggle, auth buttons, and a mobile menu
 * rendered via portal. Handles scroll-to-section behavior.
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Moon, Sun, X } from 'lucide-react';
import useDarkMode from '../../../hooks/useDarkMode';
import { useAuth } from '../../../context/auth';
import UserDropdown from '../app/UserDropdown';

// Updated scroll function
const scrollToSection = (e, id) => {
  e.preventDefault();
  const element = document.getElementById(id);
  if (!element) return;

  // Using a smaller offset to ensure proper positioning
  // Now using positive offset to scroll down instead of up
  const offset = 20;
  
  // Calculate position with new approach
  const bodyRect = document.body.getBoundingClientRect().top;
  const elementRect = element.getBoundingClientRect().top;
  const elementPosition = elementRect - bodyRect;
  const offsetPosition = elementPosition + offset; // Using addition instead of subtraction
  
  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
};

// Mobile Navigation Portal Component
function MobileNavigation({ 
  isOpen, 
  onClose, 
  isDark, 
  showAuthButtons, 
  showUserDropdown, 
  showLoading 
}) {
  const mobileLogo = isDark ? "/images/logos/logo-big-white.png" : "/images/logos/logo-big-colored.png";

  // Effect to handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      // Restore scrolling
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99999] lg:hidden">
      {/* Background overlay */}
      <div 
        className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Mobile menu panel */}
      <div className="fixed inset-y-0 right-0 z-[100000] w-full max-w-sm bg-white dark:bg-indigo-950 shadow-2xl">
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <Link
              to="/"
              className="-m-1.5 p-1.5"
              onClick={onClose}
            >
              <img 
                key={`mobile-logo-${isDark ? 'dark' : 'light'}`}
                src={mobileLogo}
                alt="Advocacy Lab"
                className="h-10 sm:h-10 w-auto"
              />
            </Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-600 hover:text-gray-900 dark:text-white dark:hover:text-gray-200 transition-colors"
              onClick={onClose}
            >
              <span className="sr-only">Close menu</span>
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="space-y-2">
              <Link
                to="/demo-templates"
                onClick={onClose}
                className="block rounded-lg px-3 py-3 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-white/10 transition-colors"
              >
                Templates
              </Link>
              <Link
                to="/done-for-you"
                onClick={onClose}
                className="block rounded-lg px-3 py-3 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-white/10 transition-colors"
              >
                Done For You
              </Link>
              {/* Partner link removed */}
              <a
                href="#pricing"
                onClick={(e) => {
                  scrollToSection(e, 'pricing');
                  onClose();
                }}
                className="block rounded-lg px-3 py-3 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-white/10 transition-colors"
              >
                Pricing
              </a>
              <a
                href="#contact"
                onClick={(e) => {
                  scrollToSection(e, 'contact');
                  onClose();
                }}
                className="block rounded-lg px-3 py-3 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-white/10 transition-colors"
              >
                Contact
              </a>
              
              {/* Auth buttons moved here, right after Contact */}
              {showAuthButtons && (
                <div className="pt-4 space-y-2">
                  <Link
                    to="/signin"
                    className="block rounded-lg border border-gray-300 bg-white px-3 py-3 text-base font-semibold text-center text-gray-900 hover:bg-gray-100 dark:border-white/20 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 transition-colors"
                    onClick={onClose}
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    className="block rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 px-3 py-3 text-base font-semibold text-center text-white shadow-md hover:shadow-lg transition-all duration-200"
                    onClick={onClose}
                  >
                    Sign up
                  </Link>
                </div>
              )}
              
              {showUserDropdown && (
                <div className="pt-4">
                  <div className="flex items-center justify-center py-2">
                    <span className="text-base font-semibold text-gray-900 dark:text-white">Welcome back!</span>
                  </div>
                </div>
              )}
              
              {showLoading && (
                <div className="pt-4">
                  <div className="flex items-center justify-center py-2">
                    <div className="animate-pulse h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Empty footer section - keeping the structure but removing the auth buttons */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-6">
            <div className="flex flex-col items-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">© {new Date().getFullYear()} Advocacy Lab</p>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function LandingHeader() {
  const isDark = useDarkMode();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, loading } = useAuth();

  const toggleTheme = () => {
    // Toggle the dark class on both document elements to match index.html behavior
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Determine logo source - fixed for light mode
  const logoSrc = isDark ? "/images/logos/logo-big-white.png" : "/images/logos/logo-big-colored.png";
  // Logo for mobile menu
  const mobileLogo = isDark ? "/images/logos/logo-big-white.png" : "/images/logos/logo-big-colored.png";

  // Determine what to show in the header based on auth state
  const showAuthButtons = !loading && !user;
  const showUserDropdown = !loading && user;
  const showLoading = loading;

  return (
    <header className="relative z-50 bg-white dark:bg-gray-900">
      <nav className="flex items-center justify-between p-4 sm:p-6 lg:px-8" aria-label="Global">
        <div className="flex flex-1 items-center justify-between gap-4">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img 
                key={`header-logo-${isDark ? 'dark' : 'light'}`}
                src={logoSrc}
                alt="Advocacy Lab"
                className="h-10 sm:h-10 w-auto"
              />
            </Link>
          </div>
          <div className="flex items-center gap-4 lg:hidden">
            <button
              onClick={toggleTheme}
              className="rounded-lg p-2 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800/50"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-white"
            >
              <span className="sr-only">Open main menu</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
          </div>

          {/* Fixed Mobile menu overlay and panel */}
          {isMobileMenuOpen && (
            <MobileNavigation
              isOpen={isMobileMenuOpen}
              onClose={closeMobileMenu}
              isDark={isDark}
              showAuthButtons={showAuthButtons}
              showUserDropdown={showUserDropdown}
              showLoading={showLoading}
            />
          )}

          <div className="hidden lg:flex lg:items-center lg:gap-x-8">
            <Link 
              to="/demo-templates"
              className="text-sm font-semibold leading-6 text-gray-700 dark:text-white hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
            >
              Templates
            </Link>
            <Link 
              to="/done-for-you"
              className="text-sm font-semibold leading-6 text-gray-700 dark:text-white hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
            >
              Done For You
            </Link>
            {/* Partner link removed */}
            <a 
              href="#pricing" 
              onClick={(e) => scrollToSection(e, 'pricing')}
              className="text-sm font-semibold leading-6 text-gray-700 dark:text-white hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
            >
              Pricing
            </a>
            <a 
              href="#contact" 
              onClick={(e) => scrollToSection(e, 'contact')}
              className="text-sm font-semibold leading-6 text-gray-700 dark:text-white hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
            >
              Contact
            </a>
            
            {/* Conditional rendering based on auth state */}
            {showAuthButtons && (
              <>
                <Link
                  to="/signin"
                  className="text-sm font-semibold leading-6 text-gray-700 dark:text-white hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 dark:from-purple-500 dark:to-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all duration-200"
                >
                  Sign up
                </Link>
              </>
            )}
            
            {showUserDropdown && (
              <UserDropdown />
            )}
            
            {showLoading && (
              <div className="flex items-center gap-2">
                <div className="animate-pulse h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="animate-pulse h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            )}

            <button
              onClick={toggleTheme}
              className="rounded-lg p-2 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Toggle dark mode"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>
    </header>
  );
}